import { useState, FormEvent } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { LogoMark } from '../components/Logo';
import { API_URL } from '../lib/api';


export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setError('Las contraseñas no coinciden'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/auth?tab=login'), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const mismatch = !!(confirm && password !== confirm);

  return (
    <div className="min-h-[calc(100vh-3.5rem)] grid place-items-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', bounce: 0, duration: 0.5 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block" aria-label="Jugaly, inicio"><LogoMark size={64} /></Link>
          <h1 className="mt-4 text-title1 font-bold text-ink">Nueva contraseña</h1>
          <p className="mt-1 text-footnote text-ink-2">Al menos 8 caracteres.</p>
        </div>

        <div className="panel shadow-card p-6">
          {!token ? (
            <div className="text-center">
              <p className="text-footnote text-danger">El enlace no es válido o ha caducado.</p>
              <Link to="/auth" className="btn-plain mt-3 text-footnote">Volver</Link>
            </div>
          ) : done ? (
            <div className="text-center py-2">
              <span className="mx-auto w-12 h-12 rounded-full bg-success/10 text-success grid place-items-center">
                <CheckCircleIcon className="w-7 h-7" />
              </span>
              <p className="mt-3 text-body font-semibold text-ink">Contraseña actualizada</p>
              <p className="mt-1 text-footnote text-ink-2">Te llevamos a iniciar sesión…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {error && <p className="text-footnote text-danger" role="alert">{error}</p>}
              <div>
                <label htmlFor="new-password" className="label">Nueva contraseña</label>
                <input id="new-password" className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required autoComplete="new-password" />
              </div>
              <div>
                <label htmlFor="confirm-password" className="label">Repite la contraseña</label>
                <input id="confirm-password" className={`input ${mismatch ? 'border-danger focus:border-danger' : ''}`} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" aria-invalid={mismatch} />
                {mismatch && <p className="mt-1.5 text-caption text-danger">Las contraseñas no coinciden.</p>}
              </div>
              <button type="submit" disabled={loading || mismatch} className="btn-primary w-full">
                {loading ? 'Guardando…' : 'Guardar contraseña'}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
